import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { GameState, Die, Message, MessageType, ChallengeResult } from '../types';
import { WebSocketService } from '../services/websocket';

interface GameContextType {
  gameState: GameState | null;
  myDice: Die[];
  myPlayerId: string | null;
  wsService: WebSocketService | null;
  lastError: string | null;
  challengeResult: ChallengeResult | null;
  connectToServer: (url: string) => Promise<void>;
  joinGame: (playerName: string, gameId?: string, playerId?: string) => void;
  addAIPlayer: () => void;
  removeAIPlayer: () => void;
  setDiceCount: (dicePerPlayer: number) => void;
  startGame: () => void;
  makeBid: (quantity: number, faceValue: number) => void;
  challenge: () => void;
  readyUp: () => void;
  leaveGame: () => void;
  clearError: () => void;
  clearChallengeResult: () => void;
  getSavedSession: () => { gameId: string; playerId: string; playerName: string } | null;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

interface GameProviderProps {
  children: React.ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [myDice, setMyDice] = useState<Die[]>([]);
  const [myPlayerId, setMyPlayerId] = useState<string | null>(null);
  const [wsService, setWsService] = useState<WebSocketService | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [challengeResult, setChallengeResult] = useState<ChallengeResult | null>(null);
  const wsServiceRef = useRef<WebSocketService | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  const isLeavingRef = useRef<boolean>(false);

  const handleMessage = useCallback((message: Message) => {
    console.log('Received message:', message);

    switch (message.type) {
      case MessageType.GAME_STATE:
        // Ignore game state updates while leaving
        if (isLeavingRef.current) {
          console.log('Ignoring GAME_STATE while leaving');
          break;
        }

        const newGameState = message.payload as GameState;
        setGameState(newGameState);
        // Sync challengeResult with game state for reconnecting clients
        if (newGameState.lastChallengeResult) {
          setChallengeResult(newGameState.lastChallengeResult);
        } else if (newGameState.phase !== 'revealing') {
          // Clear challengeResult if not in revealing phase
          setChallengeResult(null);
        }
        // Set URL fragment to game ID
        if (newGameState && newGameState.id) {
          window.location.hash = newGameState.id;
        }
        break;

      case MessageType.YOUR_DICE:
        setMyDice(message.payload.dice as Die[]);
        setMyPlayerId(message.payload.playerId);
        // Save only playerId to localStorage (gameId comes from URL fragment)
        if (message.payload.playerId) {
          localStorage.setItem('liarsDice_playerId', message.payload.playerId);
        }
        break;

      case MessageType.PLAYER_JOINED:
        // Game state will be updated via GAME_STATE message
        break;

      case MessageType.PLAYER_LEFT:
        // Game state will be updated via GAME_STATE message
        break;

      case MessageType.GAME_STARTED:
        // Game state will be updated via GAME_STATE message
        break;

      case MessageType.BID_MADE:
        // Game state will be updated via GAME_STATE message
        break;

      case MessageType.CHALLENGE_RESULT:
        setChallengeResult(message.payload as ChallengeResult);
        break;

      case MessageType.ROUND_END:
        // Handle round end - state will be updated
        break;

      case MessageType.GAME_OVER:
        // Game state will be updated via GAME_STATE message
        break;

      case MessageType.ERROR:
        const errorMessage = message.error || 'An error occurred';
        setLastError(errorMessage);

        // If game not found, clear everything and send back to main menu
        if (errorMessage.includes('Game not found')) {
          console.log('Game not found - returning to main menu');
          setGameState(null);
          setMyDice([]);
          setMyPlayerId(null);
          setChallengeResult(null);
          localStorage.removeItem('liarsDice_playerId');
          window.location.hash = '';
        }
        break;

      default:
        console.warn('Unhandled message type:', message.type);
    }
  }, []);

  const connectToServer = useCallback(async (url: string) => {
    // If already connected or currently connecting, don't reconnect
    if (isConnectingRef.current) {
      return;
    }

    if (wsServiceRef.current && wsServiceRef.current.isConnected()) {
      console.log('Already connected to WebSocket, skipping reconnection');
      return;
    }

    // Mark that we're connecting
    isConnectingRef.current = true;

    try {
      // Disconnect existing service if any
      if (wsServiceRef.current) {
        wsServiceRef.current.disconnect();
      }

      const service = new WebSocketService(url);
      service.addMessageHandler(handleMessage);

      // Set up auto-rejoin on reconnection
      service.setReconnectCallback(() => {
        console.log('WebSocket reconnected, attempting to rejoin game...');
        // Get gameId from URL fragment, playerId and playerName from localStorage
        const gameId = window.location.hash.substring(1);
        const playerId = localStorage.getItem('liarsDice_playerId');
        const playerName = localStorage.getItem('liarsDice_playerName');

        if (gameId && playerId && playerName) {
          console.log('Rejoining game with saved session:', { gameId, playerId, playerName });
          service.joinGame(playerName, gameId, playerId);
        }
      });

      await service.connect();
      wsServiceRef.current = service;
      setWsService(service);
    } finally {
      // Clear the connecting flag
      isConnectingRef.current = false;
    }
  }, [handleMessage]);

  const joinGame = useCallback((playerName: string, gameId?: string, playerId?: string) => {
    // Reset leaving flag when joining a new game
    isLeavingRef.current = false;

    // Use ref instead of state to avoid timing issues
    const service = wsServiceRef.current || wsService;
    if (service) {
      console.log('Calling wsService.joinGame with:', { playerName, gameId, playerId });
      service.joinGame(playerName, gameId, playerId);
      // Save player name to localStorage for reconnection
      localStorage.setItem('liarsDice_playerName', playerName);
    } else {
      console.error('Cannot join game - wsService not available');
    }
  }, [wsService]);

  const addAIPlayer = useCallback(() => {
    if (wsService) {
      wsService.addAIPlayer();
    }
  }, [wsService]);

  const removeAIPlayer = useCallback(() => {
    if (wsService) {
      wsService.removeAIPlayer();
    }
  }, [wsService]);

  const setDiceCount = useCallback((dicePerPlayer: number) => {
    if (wsService) {
      wsService.setDiceCount(dicePerPlayer);
    }
  }, [wsService]);

  const startGame = useCallback(() => {
    if (wsService) {
      wsService.startGame();
    }
  }, [wsService]);

  const makeBid = useCallback((quantity: number, faceValue: number) => {
    if (wsService) {
      wsService.makeBid(quantity, faceValue);
    }
  }, [wsService]);

  const challenge = useCallback(() => {
    if (wsService) {
      wsService.challenge();
    }
  }, [wsService]);

  const readyUp = useCallback(() => {
    if (wsService) {
      wsService.readyUp();
    }
  }, [wsService]);

  const leaveGame = useCallback(() => {
    // Mark that we're leaving to ignore in-flight GAME_STATE messages
    isLeavingRef.current = true;

    if (wsService) {
      wsService.leaveGame();
    }
    // Clear local game state and session
    setGameState(null);
    setMyDice([]);
    setMyPlayerId(null);
    setChallengeResult(null);
    // Clear playerId but keep player name for next game
    localStorage.removeItem('liarsDice_playerId');
    // Clear URL fragment
    window.location.hash = '';
  }, [wsService]);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const clearChallengeResult = useCallback(() => {
    setChallengeResult(null);
  }, []);

  const getSavedSession = useCallback(() => {
    // Get gameId from URL fragment, not localStorage
    const gameId = window.location.hash.substring(1);
    const playerId = localStorage.getItem('liarsDice_playerId');
    const playerName = localStorage.getItem('liarsDice_playerName');

    if (gameId && playerId && playerName) {
      return { gameId, playerId, playerName };
    }
    return null;
  }, []);

  useEffect(() => {
    return () => {
      if (wsServiceRef.current) {
        wsServiceRef.current.disconnect();
      }
    };
  }, []);

  const value: GameContextType = {
    gameState,
    myDice,
    myPlayerId,
    wsService,
    lastError,
    challengeResult,
    connectToServer,
    joinGame,
    addAIPlayer,
    removeAIPlayer,
    setDiceCount,
    startGame,
    makeBid,
    challenge,
    readyUp,
    leaveGame,
    clearError,
    clearChallengeResult,
    getSavedSession,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
